import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, EMPTY, catchError, finalize, first, tap } from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { CompanyInvoice } from 'src/app/core/models/sales/invoice.interface';
import {
  getInvoiceStatusLabelKey,
  normalizeInvoiceStatus,
} from 'src/app/core/models/sales/invoice-status.type';
import { InvoiceService } from 'src/app/core/services/invoice.service';

interface InvoiceDetailLineView {
  id: string;
  description: string;
  quantity: number;
  unitOfMeasureId: number;
  unitPrice: number;
  taxPercent: number;
  amountExcludingTax: number;
  totalTaxAmount: number;
  amountIncludingTax: number;
}

interface InvoiceDetailHeaderView {
  number: string;
  invoiceDate: string;
  dueDate: string;
  statusLabelKey: string;
  customerId: string;
  subtotal: number;
  taxTotal: number;
  total: number;
}

@Component({
  selector: 'app-invoice-detail',
  standalone: true,
  imports: [CommonModule, CoreModule, TranslateModule],
  // Encapsulation.None lets the `@media print` rules below scope to the document body so the host
  // app shell (sidebar, topbar) is hidden during print without using `::ng-deep`. The styles are
  // namespaced under `.invoice-detail` so they cannot leak into other pages.
  encapsulation: ViewEncapsulation.None,
  templateUrl: './invoice-detail.component.html',
  styleUrl: './invoice-detail.component.scss',
})
export class InvoiceDetailComponent implements OnInit {
  private readonly invoiceService = inject(InvoiceService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  public readonly invoice$ = new BehaviorSubject<CompanyInvoice | null>(null);
  public readonly headerView$ = new BehaviorSubject<InvoiceDetailHeaderView | null>(null);
  public readonly lineViews$ = new BehaviorSubject<InvoiceDetailLineView[]>([]);

  public companyId = '';
  public salesOrderId = '';
  public invoiceId = '';
  public salesOrderBackUrl = '/';
  public isLoading = false;
  public pageErrorKey = '';

  public ngOnInit(): void {
    // companyId lives on the upstream `companies/:id` route segment; the activated route only
    // carries the salesOrderId + invoiceId params for this leaf.
    this.companyId = this.route.parent?.parent?.snapshot.params['id'] ?? '';
    this.route.params
      .pipe(
        first(),
        tap((params) => {
          this.salesOrderId = params['salesOrderId'] ?? '';
          this.invoiceId = params['invoiceId'] ?? '';
          this.salesOrderBackUrl = `/companies/${this.companyId}/crm/sales-orders/${this.salesOrderId}`;
          this.loadInvoice();
        }),
      )
      .subscribe();
  }

  public retry(): void {
    this.loadInvoice();
  }

  public goBack(): void {
    this.router.navigate([this.salesOrderBackUrl]);
  }

  public print(): void {
    window.print();
  }

  private loadInvoice(): void {
    if (!this.companyId || !this.invoiceId) {
      return;
    }
    this.isLoading = true;
    this.pageErrorKey = '';

    this.invoiceService
      .getInvoice(this.companyId, this.invoiceId)
      .pipe(
        first(),
        tap((invoice) => this.applyInvoice(invoice)),
        catchError(() => {
          this.pageErrorKey = 'COMPONENTS.SALES_ORDERS.ERRORS.INVOICE_FAILED';
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  private applyInvoice(invoice: CompanyInvoice): void {
    this.invoice$.next(invoice);
    this.headerView$.next({
      number: invoice.number,
      invoiceDate: invoice.invoiceDate,
      dueDate: invoice.dueDate,
      statusLabelKey: getInvoiceStatusLabelKey(normalizeInvoiceStatus(invoice.status)),
      customerId: invoice.customerId,
      subtotal: invoice.subtotal,
      taxTotal: invoice.taxTotal,
      total: invoice.total,
    });
    // All numeric fields below are persisted snapshots — never recompute on the client.
    this.lineViews$.next(
      invoice.lines.map((line) => ({
        id: line.id,
        description: line.description ?? '',
        quantity: line.quantity,
        unitOfMeasureId: line.unitOfMeasureId,
        unitPrice: line.unitPrice,
        taxPercent: line.taxPercent,
        amountExcludingTax: line.amountExcludingTax,
        totalTaxAmount: line.totalTaxAmount,
        amountIncludingTax: line.amountIncludingTax,
      })),
    );
  }
}
